import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Dispatch, Fragment, SetStateAction, useContext, useEffect, useState } from "react";
import { SuccessAlertContext } from "App";
import { ArrowDropDown, Delete, HelpOutline, Search } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { AxiosError } from "axios";
import Notify from "config/notify";
import Header from "components/Header";
import { getUserType } from "util/auth";
import { NavigateFunction } from "react-router";
import {
  CommonForm,
  DestinationCheck,
  DirectDelivery,
  OrderBill,
  OrderConfirmErrorResponseApi,
  OrderGeneralSixBill,
  OrderGeneralSixBillErrors,
  OrderLargeClassification,
  OrderSixBill,
  OrderTyreSixBill,
  OrderTyreSixBillErrors,
  RegisterBillParam,
  RequestDirectDelivery,
  RequiredOneInSpecifiedKeys,
  SearchFormApiParams,
  Shop,
  Supplier,
  TyreOptions,
  WheelOptions,
  WholeShop,
} from "../types";
import {
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "./TableCellSmall";
import {
  addBusinessDate,
  checkBusinessDate,
  convertUTCToDate,
  localeStringDisZero,
  removeEmptyValues,
} from "../utils/common";
import downloadOrderSampleCsv from "../api/downloadOrderSampleCsv";
import LabeledIncrementalSearch from "./LabeledIncrementalSearch";
import LabeledSelect from "./LabeledSelect";
import { classificationNameList, directDeliveryOptions, productTypeOptions, requestDirectDeliveryOptions, YHClientOptions } from "../constants";
import CustomTextField from "./CustomTextField";
import Surround from "./Surround";
import LabeledText from "./LabeledText";
import registerBillApi from "../api/registerBillApi";
import { IncrementalSearchElement, SelectForm } from "./Common";
import ProductCdSearch from "./ProductCdSearch";
import getProductCompletionApi from "../api/getProductCompletionApi";
import LabeledTextField from "./LabeledTextField";
import LabeledComponent from "./LabeledComponent";
import CsvModals from "./CsvModals";
import getShopAddressInfo from "../api/getShopAddressInfo";
import LabeledButtons from "./LabeledButtons";
import CustomTooltip from "./CustomTooltip";
import downloadHelpPdf from "../api/downloadHelpPdf";

const limit = 10;

const defaultTyreOrder = (index: number): OrderTyreSixBill => ({
  id: index + 1,
  productCd: "",
  classification: "",
  supplierName: "",
  brand: "",
  productName: "",
  size: "",
  orderCount: 0,
  isSet: false,
  isStaggered: false,
  janCd: "",
  headquartersUnitCost: 0,
  wholesaleUnitPrice: 0,
  shopUnitPrice: 0,
  shopStock: 0,
  headquartersStock: {
    meiwa: 0,
    kasai: 0,
    tohoku: 0,
    itochu: 0,
    showa: 0,
  },
  supplierStock: {
    center: 0,
    neighborhood: 0,
  },
  shopRemarks: "",
  headquartersRemarks: "",
  supplierOptions: [],
  errors: {},
});

const defaultGeneralOrder = (index: number): OrderGeneralSixBill => ({
  id: index + 1,
  productCd: "",
  classification: "",
  supplierName: "",
  productName: "",
  orderCount: 0,
  janCd: "",
  headquartersUnitCost: 0,
  wholesaleUnitPrice: 0,
  shopUnitPrice: 0,
  shopStock: 0,
  headquartersStock: 0,
  supplierStock: {
    center: 0,
    neighborhood: "×",
  },
  shopRemarks: "",
  headquartersRemarks: "",
  supplierOptions: [],
  errors: {},
});

const defaultDestination: DestinationCheck = {
  destinationCd: "",
  destinationName: "",
  destinationAddress: "",
};

const styles: { [key: string]: SxProps } = {
  buttonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    position: "relative",
  },
  leftButtonContainer: {
    position: "absolute",
    top: 12,
    left: 32,
    zIndex: 1
  },
  supplierTopCol: {
    // textAlign: "center",
    position: "inherit",
    p: 0.5,
    borderBottom: "none",
    // zIndex: 999,
  },
  supplierBottomCol: {
    // textAlign: "center",
    position: "inherit",
    p: 0.5,
    top: 33,
    // zIndex: 999,
  },
};

type Props = {
  open: boolean;
  title: string;
  onClose: (initONum?: string) => void;
  orderInitList: OrderSixBill[];
  isEditMode: boolean;
  isEditModeModal: boolean; // モーダルが編集モードか作成モードか
  selectedOrderNumber: string | undefined;
  setSelectedOrderNumber: Dispatch<SetStateAction<string | undefined>>;
  setInitOrderNumber:  Dispatch<SetStateAction<string | undefined>>;
  shopCd: string; // client_cdとして使用
  setShopCd: Dispatch<SetStateAction<string>>;
  shopOptions: WholeShop[];
  getDestination: Function; // 発送先情報を取得してdestinationCheckDataにデータを入れる
  destinationCheckData: {
    A: DestinationCheck | undefined;
    B: DestinationCheck | undefined;
    C: DestinationCheck | undefined;
  };
  setDestinationCheckData: Dispatch<
    SetStateAction<{
      A: DestinationCheck | undefined;
      B: DestinationCheck | undefined;
      C: DestinationCheck | undefined;
    }>
  >;
  isDestinationLoading: boolean;
  classificationOptions: OrderLargeClassification[];
  brandOptions: string[];
  tyreOptions: TyreOptions;
  wheelOptions: WheelOptions;
  get: (params: SearchFormApiParams, isBill: boolean) => Promise<OrderBill | void>;
  onBack: Function;
  wholeSupplierOptions: Supplier[];
  isOptionsLoading: boolean;
  isBackClose?: boolean; // 戻るボタンを押した時に注文作成画面を閉じるか1つ前の画面に戻るかのフラグ。
  navigate: NavigateFunction;
};

/**
 * 注文作成・編集モーダル
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function OrderEditForm({
  open,
  title,
  onClose,
  orderInitList,
  isEditMode,
  isEditModeModal,
  selectedOrderNumber,
  setSelectedOrderNumber,
  setInitOrderNumber,
  shopCd,
  setShopCd,
  shopOptions,
  getDestination,
  destinationCheckData,
  setDestinationCheckData,
  isDestinationLoading,
  classificationOptions,
  brandOptions,
  tyreOptions,
  wheelOptions,
  get,
  onBack,
  wholeSupplierOptions,
  isOptionsLoading,
  isBackClose,
  navigate,
}: Props) {
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const [isOpenProductCdSearch, setIsOpenProductCdSearch] = useState(false);
  const [isOpenStockDetail, setIsOpenStockDetail] = useState(false); // 在庫詳細モーダル
  const [isOpenDestinationCd, setIsOpenDestinationCd] = useState(false); // 発送先コード確認モーダル
  const [isPlaceOrderCsv, setIsPlaceOrderCsv] = useState(false); // 発注CSVアップロードモーダル
  const [backupOrderList, setBackupOrderList] = useState<OrderSixBill[]>([]); // 六行伝票から削除する際に使用。
  const [orderList, setOrderList] = useState<OrderSixBill[]>(orderInitList);
  const [productType, setProductType] =
    useState<(typeof productTypeOptions)[number]>("タイヤ・ホイール");
  const [isRegisterProcessing, setIsRegisterProcessing] = useState(false);
  const [isClient, setIsClient] = useState(false); // shopCdが店舗のものか得意先のものか
  const [isYHShopBackup, setIsYHShopBackup] = useState(false); //

  // 共通作成モーダル値保持
  const [customerOrder, setCustomerOrder] = useState(false); // 客注
  const [shopOrderer, setShopOrderer] = useState("");
  const [approver, setApprover] = useState("");
  const [requestDeliveryDate, setRequestDeliveryDate] = useState(""); // 納品希望日
  // const [expectedShippingDate, setExpectedShippingDate] = useState(""); // 出荷予定日
  const [requestDirectDelivery, setRequestDirectDelivery] = useState<RequestDirectDelivery>(""); // 直送希望
  const [isDirectDelivery, setIsDirectDelivery] = useState<boolean | undefined>(undefined);
  const [clientBillNumber, setClientBillNumber] = useState("");
  // const [faxNumber, setFaxNumber] = useState("");

  // 発送先コードアコーディオン
  const [shippingDestination, setShippingDestination] = useState("");
  const [postalCd, setPostalCd] = useState("");
  const [destinationAddress, setDestinationAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [destinationName, setDestinationName] = useState("");

  // 警告メッセージのためのstate
  const [isOpenSwitchProductType, setIsOpenSwitchProductType] = useState(false);
  const [productTypeBackup, setProductTypeBackup] =
    useState<(typeof productTypeOptions)[number]>("タイヤ・ホイール"); // 選択したproductTypeを保持しておく

  // マスクを制御するローディング
  const [isLoading, setIsLoading] = useState(false); // 統一されたローディング
  const [isInitLoading, setIsInitLoading] = useState(false); // 編集モードだった場合の初回データローディング
  const [isCompletionLoading, setIsCompletionLoading] = useState(false); // 入力補完データ取得中ローディング

  // 部品のエラー
  const [approverError, setApproverError] = useState<string | undefined>(undefined);
  const [shopOrdererError, setShopOrdererError] = useState<string | undefined>(undefined);
  const [deliveryDateError, setDeliveryDateError] = useState<string | null>(null); // 納品希望日エラーメッセージ
  // const [expectedShippingDateError, setExpectedShippingDateError] = useState<string | null>(null); // 出荷予定日エラーメッセージ

  const { showAlert } = useContext(SuccessAlertContext);

  const resetValues = () => {
    setDestinationCheckData({ A: undefined, B: undefined, C: undefined });
    setOrderList([defaultTyreOrder(0)]);
    setBackupOrderList([]);
    setShopCd("");
    setProductType("タイヤ・ホイール");
    setIsClient(false);
    setCustomerOrder(false);
    setShopOrderer("");
    setApprover("");
    setRequestDeliveryDate("");
    // setExpectedShippingDate("");
    setRequestDirectDelivery("");
    setIsDirectDelivery(undefined);
    setClientBillNumber("");
    setShippingDestination("");
    setPostalCd("");
    setDestinationAddress("");
    setPhoneNumber("");
    // setFaxNumber("");
    setDestinationName("");
    setApproverError(undefined);
    setShopOrdererError(undefined);
    setDeliveryDateError(null);
    // setExpectedShippingDateError(null);
  };

  const checkGeneralBill = () => productType === "一般用品";

  const switchProductType = (isTyre: boolean) => {
    setOrderList(isTyre ? [defaultTyreOrder(0)] : [defaultGeneralOrder(0)]);
  };

  // 登録時のバリデーション
  const checkRegisterValidation = (): string | undefined => {
    let errMsg = "";
    if (orderList.length === 0) return "登録または編集する注文がありません。";
    if (!isEditModeModal && !shopCd) { return "店舗を選択してください。" }

    // タイヤ・ホイール、一般用品共通
    if (!orderList.every((o) => Number(o.orderCount) > 0)) {
      errMsg = "全てに発注数が入力してあるか確認してください。";
      const errsList: (OrderTyreSixBillErrors | OrderGeneralSixBillErrors)[] = [];
      // 発注数がない場合
      orderList.forEach((o) => {
        const errs: OrderTyreSixBillErrors | OrderGeneralSixBillErrors = {};

        if (!(o.orderCount > 0)) {
          errs.order_quantity = errMsg;
        }
        errsList.push(errs);
      });
      setOrderList((prev) => {
        // 部品ごとのエラーメッセージを挿入
        const newList = structuredClone(prev) as OrderSixBill[];
        return newList.map((n, i) => {
          // eslint-disable-next-line no-param-reassign
          n.errors = errsList[i];
          return n;
        });
      });
      return errMsg;
    }
    if (!checkGeneralBill()) {
      // タイヤ・ホイールの場合
      const oList = orderList as OrderTyreSixBill[];
      const errsList: OrderTyreSixBillErrors[] = [];
      oList.forEach((o, i) => {
        const errs: OrderTyreSixBillErrors = {};
        if (o.headquartersUnitCost == null) {
          errMsg = "本部原単価で数値を入力してください。";
          errs.unit_base_price = errMsg;
        }
        // 本部で仕切単価は空の場合店舗BOから取得するようになったのでチェックは不要
        // if (!Number(o.wholesaleUnitPrice)) {
        //   errMsg = "仕切単価の入力は必須です。";
        //   errs.unit_cost_price = errMsg;
        // }
        // 店舗売単価は未入力でもよい。デフォルトが0なので未入力 = 0とする
        if (o.shopUnitPrice == null) {
          errMsg = "店舗売単価で数値を入力してください。";
          errs.unit_sale_price = errMsg;
        }
        if (!o.productCd) {
          errMsg = "商品コードの入力は必須です。";
          errs.item_cd = errMsg;
        }
        if (o.productCd && !o.productCd.startsWith('95') && !o.classification) {
          errMsg = "有効な商品コードを入力してください。";
          errs.item_cd = errMsg;
        }
        if (o.janCd && !o.classification) {
          errMsg = "有効なJANコードを入力してください。";
          errs.jan_cd = errMsg;
        }
        if (o.productCd.slice(0, 2) === "95" && !o.shopRemarks) {
          errMsg = "代表コード(商品コードが95から始まる)商品は店舗備考の入力が必須です。";
          errs.memo = errMsg;
        }
        if (o.productCd && (o.productCd.startsWith("93") || o.productCd.startsWith("99"))) {
          errMsg = "インストアコードは許可されません。商品コードを入力してください。";
          errs.item_cd = errMsg;
        }
        if (o.productCd && o.productCd.startsWith("95") && !(o.productCd.slice(2, 4) === "02" || o.productCd.slice(2, 4) === "03")) {
          errMsg = "タイヤ・ホイールの代表コード(例:950201)を入力してください。";
          errs.item_cd = errMsg;
        }
        errsList.push(errs);
      });
      if (errMsg) {
        setOrderList((prev) => {
          // 部品ごとのエラーメッセージを挿入
          const newList = structuredClone(prev) as OrderTyreSixBill[];
          return newList.map((n, i) => {
            // eslint-disable-next-line no-param-reassign
            n.errors = errsList[i];
            return n;
          });
        });
        return errMsg;
      }

      if (oList.filter((o) => o.isStaggered).length === 0) {
        // 前後異径が含まれていない場合
        // セット商品数は0 or タイヤ・ホイールの組み合わせで2
        let setTyreCount = 0;
        let setWheelCount = 0;
        // タイヤ・ホイールを合わせて4個以上である必要がある
        // let tyreCount = 0;
        // let wheelCount = 0;
        oList.forEach((o) => {
          if (o.isSet) {
            if (o.classification.includes("タイヤ")) setTyreCount += 1;
            else if (o.classification.includes("ホイール")) setWheelCount += 1;
          }
          // if (o.classification.includes("タイヤ")) tyreCount += Number(o.orderCount);
          // else if (o.classification.includes("ホイール")) wheelCount += Number(o.orderCount);
        });
        if (
          !(setTyreCount === 0 && setWheelCount === 0) &&
          !(setTyreCount === 1 && setWheelCount === 1)
        )
          return "セット商品は1伝票につき、0組または1組(=タイヤとホイールで1商品ずつ)のみ選択可能です。";
        // if (tyreCount > 0 && tyreCount < 4) return "タイヤは合計4本以上から発注できます。";
        // if (wheelCount > 0 && wheelCount < 4) return "ホイールは合計4本以上から発注できます。";
      } else {
        // セット商品数は0 or タイヤ・ホイールの組み合わせで2
        let setTyreCount = 0;
        let setWheelCount = 0;
        // タイヤ・ホイールを合わせて4個以上である必要がある
        // let tyreCount = 0;
        // let wheelCount = 0;
        // 同一商品でセットと前後異径は選択不可
        // let setStaggeredFlag = false;
        const errsTyreList: OrderTyreSixBillErrors[] = [];
        oList.forEach((o) => {
          const errs: OrderTyreSixBillErrors = {};
          if (o.isSet) {
            if (o.classification.includes("タイヤ")) setTyreCount += 1;
            else if (o.classification.includes("ホイール")) setWheelCount += 1;
          }
          // if (o.isSet && o.isStaggered) {
          //   errs.is_set =
          //     "タイヤ・ホイールの同一商品に前後異径とセット両方を選択することはできません。";
          //   errs.is_staggered =
          //     "タイヤ・ホイールの同一商品に前後異径とセット両方を選択することはできません。";
          //   setStaggeredFlag = true;
          // }
          // if (o.classification === "タイヤ") tyreCount += Number(o.orderCount);
          // else if (o.classification === "ホイール") wheelCount += Number(o.orderCount);
          errsTyreList.push(errs);
        });
        if (
          !(setTyreCount === 0 && setWheelCount === 0) &&
          !(setTyreCount === 1 && setWheelCount === 1)
        )
          return "セット商品は1伝票につき、0組または1組(=タイヤとホイールで1商品ずつ)のみ選択可能です。";
        // if (setStaggeredFlag) {
        //   errMsg = "タイヤ・ホイールの同一商品に前後異径とセット両方を選択することはできません。";
        //   setOrderList((prev) => {
        //     // 部品ごとのエラーメッセージを挿入
        //     const newList = structuredClone(prev) as OrderTyreSixBill[];
        //     return newList.map((n, i) => {
        //       // eslint-disable-next-line no-param-reassign
        //       n.errors = errsTyreList[i];
        //       return n;
        //     });
        //   });
        //   return errMsg;
        // }
        // if (tyreCount > 0 && tyreCount < 4) return "タイヤは合計4本以上から発注できます。";
        // if (wheelCount > 0 && wheelCount < 4) return "ホイールは合計4本以上から発注できます。";
      }
    } else {
      const errsList: OrderGeneralSixBillErrors[] = [];
      orderList.forEach((o, i) => {
        const errs: OrderGeneralSixBillErrors = {};
        if (o.headquartersUnitCost == null) {
          errMsg = "本部原単価で数値を入力してください。";
          errs.unit_base_price = errMsg;
        }
        // 本部で仕切単価は空の場合店舗BOから取得するようになったのでチェックは不要
        // if (!Number(o.wholesaleUnitPrice)) {
        //   errMsg = "仕切単価の入力は必須です。";
        //   errs.unit_cost_price = errMsg;
        // }
        if (o.shopUnitPrice == null) {
          errMsg = "店舗売単価で数値を入力してください。";
          errs.unit_sale_price = errMsg;
        }
        if (!o.productCd && !o.janCd) {
          errMsg = "商品コードまたはJANコードの入力は必須です。";
          errs.item_cd = errMsg;
          errs.jan_cd = errMsg;
        }
        if (o.productCd && !o.productCd.startsWith('95') && !o.classification) {
          errMsg = "有効な商品コードを入力してください。";
          errs.item_cd = errMsg;
        }
        if (o.janCd && !o.classification) {
          errMsg = "有効なJANコードを入力してください。";
          errs.jan_cd = errMsg;
        }
        if (o.productCd.slice(0, 2) === "95" && !o.shopRemarks) {
          errMsg = "代表コード(商品コードが95から始まる)商品は店舗備考の入力が必須です。";
          errs.memo = errMsg;
        }
        if (o.productCd && (o.productCd.startsWith("93") || o.productCd.startsWith("99"))) {
          errMsg = "インストアコードは許可されません。商品コードを入力してください。";
          errs.item_cd = errMsg;
        }
        if (o.productCd && o.productCd.startsWith("95") && (o.productCd.slice(2, 4) === "02" || o.productCd.slice(2, 4) === "03")) {
          errMsg = "一般用品の代表コード(例:950401)を入力してください。";
          errs.item_cd = errMsg;
        }
        errsList.push(errs);
      });
      if (errMsg) {
        setOrderList((prev) => {
          // 部品ごとのエラーメッセージを挿入
          const newList = structuredClone(prev) as OrderGeneralSixBill[];
          return newList.map((n, i) => {
            // eslint-disable-next-line no-param-reassign
            n.errors = errsList[i];
            return n;
          });
        });
        return errMsg;
      }
      if (!shopOrderer) {
        errMsg = "店舗発注者の入力は必須です。";
        setShopOrdererError(errMsg);
        return errMsg;
      }
      if (!approver) {
        errMsg = "承認者の入力は必須です。";
        setApproverError(errMsg);
        return errMsg;
      }
    }
    if (deliveryDateError) return deliveryDateError;
    // if (expectedShippingDateError) return expectedShippingDateError;
    return undefined;
  };

  const handleRegisterBill = async (): Promise<string | boolean> => {
    const errMsg = checkRegisterValidation();
    if (errMsg) {
      // 入力内容に不備がある場合
      showAlert(errMsg, "error");
      return false;
    }
    setIsRegisterProcessing(true);
    try {
      const promises: Promise<string[]>[] = [];
      const params: RegisterBillParam = removeEmptyValues({
        client_cd: shopCd, // 編集だった場合はclient_cdを渡す（更新にも発注先等のバリデーションに必要らしい）
        is_delete: 0,
        order_list: orderList.map(o => removeEmptyValues({
          order_build_no: o.orderNumber,
          order_no: 'orderPlainNumber' in o ? o.orderPlainNumber : undefined,
          item_cd: o.productCd,
          jan_cd: o.janCd,
          item_name: o.productName,
          supplier_cd: o.productCd.startsWith("95")
            // ? wholeSupplierOptions.find(wso => wso.supplierName === o.supplierName)?.supplierCd
            ? o.supplierCd
            : o.supplierOptions.find(so => so.supplierName === o.supplierName)?.supplierCd,
          order_quantity: o.orderCount,
          unit_base_price: o.headquartersUnitCost,
          unit_cost_price: o.wholesaleUnitPrice,
          unit_sale_price: o.shopUnitPrice,
          is_set_order: 'isSet' in o ? Number(o.isSet) : 0,
          is_staggered: 'isStaggered' in o ? Number(o.isStaggered) : 0,
          is_customer_order: Number(customerOrder),
          is_direct_shipping: requestDirectDelivery !== "" ? Number(requestDirectDelivery === "メーカー直送で納品") : null, // 本部も直送希望を送信するよう変更。nullの場合も送信する。
          author: shopOrderer,
          approver,
          client_doc_no: clientBillNumber,
          desired_date: convertUTCToDate(requestDeliveryDate),
          // shipping_scheduled: convertUTCToDate(expectedShippingDate),
          shipping_dest_code: shippingDestination.split(" ")[0],
          shipping_dest_zip: postalCd,
          shipping_dest_address: destinationAddress,
          shipping_dest_tel: phoneNumber,
          // order_store_fax: faxNumber,
          shipping_dest_name: destinationName,
          memo: o.shopRemarks,
          remarks: o.headquartersRemarks,
        }, [], undefined, true))
      });
      promises.push(registerBillApi(params));
      if (isEditModeModal) {
        // 編集で削除されたものがあれば削除フラグを立てて追加
        const deletedOrderList = backupOrderList.filter(
          (bo) => !orderList.some((o) => !!o.orderNumber && o.orderNumber === bo.orderNumber),
        );
        if (deletedOrderList) {
          const dParams: RegisterBillParam = removeEmptyValues({
            // client_cd: !isEditModeModal ? shopCd : undefined,
            is_delete: 1,
            order_list: deletedOrderList.map(o => removeEmptyValues({
              order_build_no: o.orderNumber,
              item_cd: o.productCd,
              jan_cd: o.janCd,
              item_name: o.productName,
              supplier_cd: o.productCd.startsWith("95")
                // ? wholeSupplierOptions.find(wso => wso.supplierName === o.supplierName)?.supplierCd
                ? o.supplierCd
                : o.supplierOptions.find(so => so.supplierName === o.supplierName)?.supplierCd,
              order_quantity: o.orderCount,
              unit_base_price: o.headquartersUnitCost,
              unit_cost_price: o.wholesaleUnitPrice,
              unit_sale_price: o.shopUnitPrice,
              is_set_order: 'isSet' in o ? Number(o.isSet) : 0,
              is_staggered: 'isStaggered' in o ? Number(o.isStaggered) : 0,
              is_customer_order: Number(customerOrder),
              is_direct_shipping: requestDirectDelivery !== "" ? Number(requestDirectDelivery === "メーカー直送で納品") : null, // 本部も直送希望を送信するよう変更
              author: shopOrderer,
              approver,
              client_doc_no: clientBillNumber,
              desired_date: convertUTCToDate(requestDeliveryDate),
              // shipping_scheduled: convertUTCToDate(expectedShippingDate),
              shipping_dest_code: shippingDestination.split(" ")[0],
              shipping_dest_zip: postalCd,
              shipping_dest_address: destinationAddress,
              shipping_dest_tel: phoneNumber,
              // order_store_fax: faxNumber,
              shipping_dest_name: destinationName,
              memo: o.shopRemarks,
              remarks: o.headquartersRemarks,
            }, [], undefined, true)),
          });
          if (dParams.order_list.length > 0) {
            promises.push(registerBillApi(dParams));
          }
        }
      }
      const [oNumArray] = await Promise.all(promises);
      const oNum = oNumArray.join(",");
      if (isEditMode) {
        // 全体が編集モードの場合。selectedOrderNumberはonCloseで処理している。
        setInitOrderNumber(oNum);
      }

      showAlert("登録しました。", "success");
      return oNum || true;
    } catch (err) {
      const error = err as AxiosError;
      console.error(err);
      // レスポンス自体がなくエラーになった時
      if (!error.response) {
        Notify.setNotification({
          show: true,
          title: "APIに接続できませんでした。",
          confirmLabel: "OK",
          actionsPosition: "end",
        });
        return false;
      }
      const { status } = error.response;
      const data = error.response.data as OrderConfirmErrorResponseApi;
      switch (status) {
        case 400:
          Notify.setNotification({
            show: true,
            title: "パラメータエラーです。",
            confirmLabel: "OK",
            actionsPosition: "end",
          });
          break;
        case 422:
          Notify.setNotification({
            // show: true,
            // title: data.messages.join('\n'),
            // confirmLabel: "OK",
            // actionsPosition: "end",
            show: true,
            title: "入力エラーがあります。",
            message: data.messages.join("\n"),
            titleColor: "var(--error)",
          });
          // APIでエラー箇所をエラー表示にする
          // eslint-disable-next-line no-case-declarations
          const oList = structuredClone(orderList);
          data.order_list.forEach((eo) => {
            if (eo.columns.length > 0) {
              eo.columns.forEach((col) => {
                // 6行伝票の中のパラメータの場合
                if (
                  [
                    "item_cd",
                    "jan_cd",
                    "unit_cost_price",
                    "unit_base_price",
                    "unit_sale_price",
                    "order_quantity",
                    "item_name",
                    "memo",
                    "remarks",
                    "is_set",
                    "is_staggered",
                    "order_no",
                  ].includes(col)
                ) {
                  oList[eo.row].errors[
                    col as keyof (OrderGeneralSixBillErrors | OrderTyreSixBillErrors)
                  ] = "エラーがあります。";
                } else if (col === "approver") setApprover("正しく承認者を設定してください。");
                else if (col === "author")
                  setShopOrdererError("正しく店舗発注者を設定してください。");
                else if (col === "desired_date")
                  setDeliveryDateError("正しく納品希望日を設定してください。");
                // else if (col === "shipping_scheduled")
                //   setExpectedShippingDateError("正しく出荷予定日を設定してください。");
              });
            }
          });
          break;
        default:
          break;
      }
      return false;
    } finally {
      setIsRegisterProcessing(false);
    }
  };

  const cloneListRewrite = <T extends object, K extends keyof T>(
    prev: T[],
    key: K,
    value: T[K],
    index: number,
  ) => {
    const clonePrev = structuredClone(prev);
    clonePrev[index][key] = value;
    return clonePrev;
  };

  const handleSearch = (index: number) => {
    if (!shopCd) {
      showAlert('先に店舗名を選択してください。', 'error');
      return;
    }
    setSelectedIndex(index);
    setIsOpenProductCdSearch(true);
  };

  const handleDelete = (index: number) => {
    setOrderList((prev) => {
      const newOrder = structuredClone(prev);
      // 注文テーブルの一つを削除
      newOrder.splice(index, 1);
      return newOrder;
    });
  };

  const handleInputCompletion = async (
    index: number,
    isTyre: boolean,
    pjCd: RequiredOneInSpecifiedKeys<{ pCd?: string; jCd?: string }, "pCd" | "jCd">,
  ) => {
    if (
      (pjCd.pCd?.length === 6 && pjCd.pCd.slice(0, 2) !== "95") ||
      (pjCd.jCd?.length === 13 &&
        !(
          orderList[index].productCd.length === 6 && orderList[index].productCd.slice(0, 2) === "95"
        ))
    ) {
      try {
        setIsCompletionLoading(true);
        if (!shopCd) {
          showAlert('先に店舗名を選択してください。', 'error');
          return;
        }
        const params = pjCd.jCd === undefined
          ? {item_cd: pjCd.pCd ?? '', client_cd: shopCd}
          : {jan_cd: pjCd.jCd ?? '', client_cd: shopCd};
        const res = await getProductCompletionApi(params, isTyre, index);
        if (res) {
          console.log('res comp:', res);
          if ((res.classification !== "タイヤ" && res.classification !== "ホイール") && productType === "タイヤ・ホイール") {
            // タイヤ・ホイールなのに商品コードがタイヤ・ホイールではなかった場合
            showAlert('大分類がタイヤ・ホイールの物を選択してください。', 'error');
            setOrderList((prev) => {
              const newOrders = structuredClone(prev);
              newOrders[index].productCd = "";
              return newOrders;
            });
            return;
          }
          if ((res.classification === "タイヤ" || res.classification === "ホイール") && productType === "一般用品") {
            // 一般用品なのに商品コードがタイヤ・ホイールだった場合
            showAlert('大分類が一般用品の物を選択してください。', 'error');
            setOrderList((prev) => {
              const newOrders = structuredClone(prev);
              newOrders[index].productCd = "";
              return newOrders;
            });
            return;
          }
          setOrderList((prev) => {
            const newOrders = structuredClone(prev);
            if ('orderPlainNumber' in newOrders[index] && newOrders[index].orderPlainNumber) (res as OrderGeneralSixBill).orderPlainNumber = newOrders[index].orderPlainNumber;
            if (newOrders[index].orderNumber) res.orderNumber = newOrders[index].orderNumber;
            res.shopRemarks = newOrders[index].shopRemarks;
            res.headquartersRemarks = newOrders[index].headquartersRemarks;
            res.orderCount = newOrders[index].orderCount;
            newOrders[index] = res;
            return newOrders;
          });
        }
      } catch (err) {
        console.error(err);
        setOrderList(prev => {
          const newOrders = structuredClone(prev);
          newOrders[index].classification = "";
          newOrders[index].supplierName = "";
          newOrders[index].supplierOptions = [];
          if ("brand" in newOrders[index]) newOrders[index].brand = "";
          newOrders[index].productName = "";
          if ("size" in newOrders[index]) newOrders[index].size = "";
          newOrders[index].janCd = "";
          newOrders[index].shopStock = 0;
          if (isTyre) {
            newOrders[index].headquartersStock = {
              meiwa: 0,
              kasai: 0,
              tohoku: 0,
              itochu: 0,
              showa: 0,
            }
          } else newOrders[index].headquartersStock = 0

          newOrders[index].supplierStock = {
            center: 0,
            neighborhood: 0,
          }
          return newOrders
        });
      } finally {
        setIsCompletionLoading(false);
      }
    } else if (pjCd.pCd?.slice(0, 2) === "95") {
      const classCd = Number(pjCd.pCd.slice(2,4))
      if (classificationNameList[classCd]) {
        setOrderList(prev => {
          const res = isTyre ? defaultTyreOrder(0) : defaultGeneralOrder(0);
          const newOrders = structuredClone(prev);
          if ('orderPlainNumber' in newOrders[index] && newOrders[index].orderPlainNumber) (res as OrderGeneralSixBill).orderPlainNumber = newOrders[index].orderPlainNumber;
          if (newOrders[index].orderNumber) res.orderNumber = newOrders[index].orderNumber;
          res.productCd = newOrders[index].productCd;
          res.shopRemarks = newOrders[index].shopRemarks;
          res.headquartersRemarks = newOrders[index].headquartersRemarks;
          res.orderCount = newOrders[index].orderCount;
          newOrders[index] = res;
          newOrders[index].classification = classificationNameList[classCd];
          return newOrders
        })
      }
    }
  };

  // 6行伝票以上になっていないかチェック
  const checkFullCost = (exceedLine?: number) => {
    let cost = 0;
    orderList.forEach((o) => {
      const isTyre = "isStaggered" in o;
      if (isTyre && (o as OrderTyreSixBill).isSet) cost += 0.5;
    });
    cost += orderList.length;
    return cost >= (exceedLine ?? 6);
  };

  const actionButtons = () => (
    <>
      <Button variant="custom-outlined" onClick={() => onClose()}>
        {isEditModeModal ? "編集せずに注文一覧確認" : "登録せずに注文一覧確認"}
      </Button>
      {!isEditModeModal && (
        <Button
          variant="contained"
          onClick={async () => {
            const f = await handleRegisterBill();
            if (f) resetValues();
          }}
          disabled={isRegisterProcessing}
        >
          登録
        </Button>
      )}
      <Button
        variant="contained"
        onClick={async () => {
          const f = await handleRegisterBill();
          if (f) onClose(isEditModeModal && typeof f === "string" ? f : undefined);
        }}
        disabled={isRegisterProcessing}
      >
        {isEditModeModal ? "編集して注文一覧確認" : "登録して注文一覧確認"}
      </Button>
    </>
  );

  // 注文作成・編集モーダルの単票部分を埋める
  const fillCommonForm = (form: CommonForm) => {
    setCustomerOrder(form.customerOrder);
    setShopOrderer(form.shopOrderer);
    setApprover(form.approver);
    setRequestDeliveryDate(form.requestDeliveryDate);
    // setExpectedShippingDate(form.expectedShippingDate);
    setRequestDirectDelivery(form.requestDirectDelivery);
    setIsDirectDelivery(form.isDirectDelivery);
    setClientBillNumber(form.clientBillNumber);
    setShippingDestination(form.shippingDestination);
    setPostalCd(form.postalCd);
    setDestinationAddress(form.destinationAddress);
    setPhoneNumber(form.phoneNumber);
    setDestinationName(form.destinationName);
  };

  // APIから店舗の住所等のデフォルトの発送先を取得する
  const getDestinationInfo = async (cCd?: string) => {
    if (!cCd) {
      showAlert('先に店舗名を選択してください。', 'error');
      return;
    }
    const res = await getShopAddressInfo(cCd);
    setPostalCd(res.postalCd);
    setDestinationAddress(res.address);
    setPhoneNumber(res.phoneNumber);
    setDestinationName(res.destinationName);
  };

  useEffect(() => {
    (async () => {
      if (open) {
        if (!isEditModeModal) {
          setOrderList([defaultTyreOrder(0)]);
        } else {
          try {
            setIsInitLoading(true);
            // 注文情報取得
            const params: Omit<SearchFormApiParams, "limit" | "offset"> = {
              screen_type: 21,
              sort_type: 2,
              order_build_no: selectedOrderNumber, // AA1234567899-01の左側（右側は同じ伝票の注文商品を区別するシーケンシャル番号）
            };
            const orderBill = (await get(params, true)) as OrderBill;
            const baseBills = orderBill.tableList;
            // const isTyre = "isStaggered" in baseBills[0]; // 前後異径を含んでいればタイヤ
            // is_tire_wheelがあればそれでタイヤ・ホイール/一般用品を判断、なければ前後異径で判断
            const isTyre = baseBills[0].productCd && baseBills[0].productCd.startsWith('95') && (baseBills[0].productCd.slice(2,4) === "02" || baseBills[0].productCd.slice(2,4) === "03") ? true // 代表コードではタイヤ・ホイールはAPI側でfalseになるので画面側で画面側かどうかを判断。
              : ((baseBills[0].classification == null || baseBills[0].classification === "") && baseBills[0].productType !== undefined) ? baseBills[0].productType // 大分類がなければ（商品コードがない場合など）productTypeで判断
              : "isStaggered" in baseBills[0];
            const getPromises = baseBills.map((bill, index) => {
              console.log(isTyre);
              // const completionParams = { item_cd: bill.productCd, client_cd: bill.clientCd };
              let completionParams;
              if (bill.productCd && !bill.productCd.startsWith('95'))
                completionParams = { item_cd: bill.productCd, client_cd: bill.clientCd };
              else if (bill.janCd)
                completionParams = { jan_cd: bill.janCd, client_cd: bill.clientCd }
              else {
                // productCd, janCdがない場合は補完は必要ない
                return Promise.resolve(null);
              }
              return getProductCompletionApi(completionParams, isTyre, index);
            });
            const completionBills = await Promise.all(getPromises);
            const initBills = baseBills.map((bBill, index) => {
              const cBill = completionBills[index];
              const nBill = structuredClone(bBill);
              if (cBill) {
                // 商品コードが登録されている場合は取得した補完情報もプラスする
                nBill.headquartersStock = cBill.headquartersStock;
                nBill.shopStock = cBill.shopStock;
                nBill.supplierStock = cBill.supplierStock;
                nBill.supplierOptions = cBill.supplierOptions;
              }
              return nBill;
            });
            setShopCd(prev => initBills[0].clientCd ?? prev);
            if (!isOptionsLoading) setIsClient(prev => shopOptions.find(so => so.clientCd === orderList[0].clientCd)?.isClient ?? true); // 画面を閉じた後に編集で開いた場合にisClientを入れ直す
            setProductType(isTyre ? "タイヤ・ホイール" : "一般用品");
            setOrderList(initBills);
            setBackupOrderList(structuredClone(initBills)); // バックアップを取る。六行伝票から注文を削除して更新する際に使用。
            fillCommonForm(orderBill.commonForm);
          } catch (err) {
            console.error(err);
            if (err) {
              const errData = (err as AxiosError).response?.data as any;
              if (errData.message && errData.message === "指定された商品情報は存在しません。") {
                showAlert("得意先の場合はジョイフルで商品設定されているかご確認ください。", "error");
                onClose();
              }
            }
          } finally {
            setIsInitLoading(false);
          }
        }
      } else {
        // 閉じる時には値をリセット
        setIsOpenDestinationCd(false);
        setIsPlaceOrderCsv(false);
        resetValues();
      }
    })();
  }, [open]);

  useEffect(() => {
    setTimeout(async () => {
      if (isEditModeModal && shopOptions.length > 0 && orderList.length > 0 && !isOptionsLoading && !isInitLoading) {
        const cCd: string | undefined = orderList[0].clientCd;
        const isC: boolean | undefined = shopOptions.find(so => so.clientCd === orderList[0].clientCd)?.isClient;
        console.log("isClient:", shopOptions, orderList[0], isC);
        setIsClient(prev => isC ?? true);
        if (isC) {
          // 得意先情報取得
          await getDestination(cCd);
        }
      }
    }, 0);
  }, [isOptionsLoading, isInitLoading]);

  // ローディング統一
  useEffect(() => {
    setIsLoading(isRegisterProcessing || isInitLoading || isOptionsLoading || isCompletionLoading);
  }, [isRegisterProcessing, isInitLoading, isOptionsLoading, isCompletionLoading]);

  const tyreTable = () => (
    <Fragment key="1">
      <TableHead>
        <TableRow>
          <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
            商品コード検索
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>商品コード</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>大分類</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>発注先</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>ブランド</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol} colSpan={2}>
            ｼｮｳﾋﾝﾒｲ(半角ｶﾅ)
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>サイズ</TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierTopCol}>
            発注数(
            <Typography display="inline" color="red">
              必須
            </Typography>
            )
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
            セット
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
            前後異径
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
            削除
          </TableHeaderCellNarrow>
        </TableRow>
        <TableRow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>JANコード</TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            仕切単価
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            本部原単価
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            店舗売単価
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            店舗在庫
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            本部倉庫在庫
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            仕入先在庫
            <br />
            センター倉庫
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            仕入先在庫
            <br />
            近隣倉庫
          </TableHeaderCellNarrow>
        </TableRow>
      </TableHead>

      <TableBody>
        {orderList.map((order, index) => {
          const isZebraStripeRow = index % 2 === 1;
          // eslint-disable-next-line no-param-reassign
          order = order as OrderTyreSixBill;
          return (
            <Fragment key={order.id}>
              <TableRow key={order.id}>
                <TableCellNarrow center gray={isZebraStripeRow} rowSpan={4}>
                  <Box sx={{ cursor: "pointer" }} onClick={() => handleSearch(index)}>
                    <Search />
                  </Box>
                </TableCellNarrow>
                <TableCellNarrow gray={isZebraStripeRow}>
                  <CustomTextField
                    id="product_cd"
                    label="商品コード"
                    maxLength={100}
                    text={order.productCd}
                    setText={(val: string) => {
                      setOrderList((prev) => cloneListRewrite(prev, "productCd", val, index));
                      handleInputCompletion(index, true, { pCd: val });
                    }}
                    isNumericText
                    error={order.errors.item_cd}
                  />
                </TableCellNarrow>

                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.classification}
                </TableCellNarrow>
                <TableCellNarrow gray={isZebraStripeRow} sx={{ px: 0, py: 0 }}>
                  { order.productCd.startsWith("95")
                    ?
                      // <IncrementalSearchElement
                      //   key="supplier_name"
                      //   label="発注先"
                      //   options={wholeSupplierOptions.map((so) => so.supplierName)}
                      //   value={order.supplierName}
                      //   onChange={(val: string) =>
                      //     setOrderList((prev) => cloneListRewrite(prev, "supplierName", val, index))
                      //   }
                      //   optionLength={10}
                      //   isBlankNoOptions
                      //   fullWidth
                      // />
                      <CustomTextField
                        id="supplier_cd"
                        label="発注先コード"
                        maxLength={6}
                        text={order.supplierCd ?? ''}
                        setText={(val: string) => {
                          setOrderList((prev) => cloneListRewrite(prev, "supplierCd", val, index))
                        }}
                        isNumericText
                        sx={{px: 1}}
                        // fullWidth
                      />
                    : <SelectForm
                        key="supplier_name"
                        id="supplier_name"
                        label="発注先"
                        optionList={order.supplierOptions.map((c) => c.supplierName)}
                        value={order.supplierName}
                        setValue={(val: string) =>
                          setOrderList((prev) => cloneListRewrite(prev, "supplierName", val, index))
                        }
                        fullWidth
                        minWidth={80}
                        isContainBlank
                        allowValueNotInOptions
                      />
                  }
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.brand}
                </TableCellNarrow>
                <TableCellNarrow center gray={isZebraStripeRow} colSpan={2}>
                  <CustomTextField
                    id="product_name"
                    label="ｼｮｳﾋﾝﾒｲ(半角ｶﾅ)"
                    text={order.productName}
                    setText={(val: string) =>
                      setOrderList((prev) => cloneListRewrite(prev, "productName", val, index))
                    }
                    fullWidth
                    maxWidth={200}
                    error={order.errors.item_name}
                    allowHalfKanaOnly
                  />
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.size}
                </TableCellNarrow>
                <TableCellNarrow right gray={isZebraStripeRow}>
                  <CustomTextField
                    type="Number"
                    id="order_count"
                    label="発注数"
                    text={`${order.orderCount}`}
                    setText={(val) => {
                      if (val < 0 || val > 99999) {
                        // 発注数は5桁まで
                        return;
                      }
                      setOrderList((prev) => cloneListRewrite(prev, "orderCount", val, index))
                    }}
                    isNumericText
                    intMax={99999}
                    error={order.errors.order_quantity}
                  />
                </TableCellNarrow>
                <TableCellNarrow center gray={isZebraStripeRow} rowSpan={2}>
                  <Checkbox
                    color={order.errors.is_set ? "error" : "secondary"}
                    checked={order.isSet}
                    onChange={(_, check) => {
                      if (checkFullCost() && check) {
                        showAlert("六行伝票の6行を超えています(セットは2つで+1行)", "error");
                      } else {
                        setOrderList((prev) => {
                          const newOrders = structuredClone(prev) as OrderTyreSixBill[];
                          newOrders[index].isSet = check;
                          if (!check) newOrders[index].isStaggered = false;
                          return newOrders;
                        });
                      }
                    }}
                    inputProps={{ "aria-label": "set-selected" }}
                  />
                </TableCellNarrow>
                <TableCellNarrow center gray={isZebraStripeRow} rowSpan={2}>
                  <CustomTooltip title={!order.isSet ? "先にセットを指定してください。" : ""} disableInteractive arrow>
                    <Typography component="span">
                      <Checkbox
                        color={order.errors.is_staggered ? "error" : "secondary"}
                        checked={order.isStaggered}
                        onChange={(_, check) => {
                          setOrderList((prev) => {
                            const newOrders = structuredClone(prev) as OrderTyreSixBill[];
                            newOrders[index].isStaggered = check;
                            return newOrders;
                          });
                        }}
                        inputProps={{ "aria-label": "set-selected" }}
                        disabled={!order.isSet}
                      />
                    </Typography>
                  </CustomTooltip>
                </TableCellNarrow>
                <TableCellNarrow center gray={isZebraStripeRow} rowSpan={4}>
                  <Box sx={{ cursor: "pointer" }} onClick={() => handleDelete(index)}>
                    <Delete />
                  </Box>
                </TableCellNarrow>
              </TableRow>
              <TableRow>
                <TableCellNarrow gray={isZebraStripeRow}>
                  <CustomTextField
                    id="jan_cd"
                    label="JANコード"
                    maxLength={100}
                    text={order.janCd}
                    setText={(val: string) => {
                      setOrderList((prev) => cloneListRewrite(prev, "janCd", val, index));
                      handleInputCompletion(index, true, { jCd: val });
                    }}
                    isNumericText
                    error={order.errors.jan_cd}
                  />
                </TableCellNarrow>
                <TableCellNarrow gray={isZebraStripeRow}>
                  <CustomTextField
                    type="Number"
                    id="wholesale_unit_price"
                    label="仕切単価"
                    text={`${order.wholesaleUnitPrice}`}
                    setText={(val) => {
                      if (val < 0 || val > 9999999) {
                        // 発注数は7桁まで
                        return;
                      }
                      setOrderList((prev) =>
                        cloneListRewrite(prev, "wholesaleUnitPrice", val, index),
                      )
                    }}
                    isNumericText
                    intMax={9999999}
                    error={order.errors.unit_cost_price}
                  />
                </TableCellNarrow>
                <TableCellNarrow gray={isZebraStripeRow}>
                  <CustomTextField
                    type="Number"
                    id="headquarters_unit_cost"
                    label="本部原単価"
                    text={`${order.headquartersUnitCost}`}
                    setText={(val) => {
                      if (val < 0 || val > 9999999) {
                        // 発注数は7桁まで
                        return;
                      }
                      setOrderList((prev) =>
                        cloneListRewrite(prev, "headquartersUnitCost", val, index),
                      )
                    }}
                    isNumericText
                    intMax={9999999}
                    error={order.errors.unit_base_price}
                  />
                </TableCellNarrow>
                <TableCellNarrow gray={isZebraStripeRow}>
                  <CustomTextField
                    type="Number"
                    id="shop_unit_price"
                    label="店舗売単価"
                    text={`${order.shopUnitPrice}`}
                    setText={(val) => {
                      if (val < 0 || val > 9999999) {
                        // 発注数は7桁まで
                        return;
                      }
                      setOrderList((prev) => cloneListRewrite(prev, "shopUnitPrice", val, index))
                    }}
                    isNumericText
                    intMax={9999999}
                    error={order.errors.unit_sale_price}
                  />
                </TableCellNarrow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {isClient ? "" : order.shopStock}
                </TableCellNarrow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  <Box
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedIndex(index);
                      setIsOpenStockDetail(true);
                    }}
                  >
                    <Typography fontSize={16} color="blue" sx={{ textDecoration: "underline" }}>
                      {Math.max(...Object.values(order.headquartersStock))}
                    </Typography>
                  </Box>
                </TableCellNarrow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {order.supplierStock.center}
                </TableCellNarrow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {order.supplierStock.neighborhood}
                </TableCellNarrow>
              </TableRow>
              <TableRow>
                <TableCellWide gray={isZebraStripeRow} colSpan={10}>
                  <Box display="flex" flexDirection="row">
                    <Checkbox
                      color="secondary"
                      onChange={(_, check) => {
                        if (check)
                          setOrderList((prev) =>
                            cloneListRewrite(
                              prev,
                              "shopRemarks",
                              `${order.shopRemarks} 電話/FAXで注文済み `,
                              index,
                            ),
                          );
                      }}
                      inputProps={{ "aria-label": "order-by-tel-or-fax-selected" }}
                    />
                    <Box minWidth={140} alignContent="center">
                      <Typography>電話/FAXで注文済</Typography>
                    </Box>
                    <Box width="100%" margin={1}>
                      <TextField
                        type="text"
                        label="店舗備考"
                        size="small"
                        value={order.shopRemarks}
                        onChange={(e) =>
                          setOrderList((prev) =>
                            cloneListRewrite(prev, "shopRemarks", e.target.value, index),
                          )
                        }
                        fullWidth
                        error={!!order.errors.memo}
                        inputProps={{ maxLength: 256 }}
                      />
                    </Box>
                  </Box>
                </TableCellWide>
              </TableRow>
              <TableRow>
                <TableCellWide gray={isZebraStripeRow} colSpan={10}>
                  <Box display="flex" flexDirection="row">
                    <Checkbox
                      color="secondary"
                      onChange={(_, check) => {
                        if (check)
                          setOrderList((prev) =>
                            cloneListRewrite(
                              prev,
                              "headquartersRemarks",
                              `${order.headquartersRemarks} 電話/FAXで注文済み `,
                              index,
                            ),
                          );
                      }}
                      inputProps={{ "aria-label": "order-by-tel-or-fax-selected" }}
                    />
                    <Box minWidth={140} alignContent="center">
                      <Typography>電話/FAXで注文済</Typography>
                    </Box>
                    <Box width="100%" margin={1}>
                      <TextField
                        type="text"
                        label="本部備考"
                        size="small"
                        value={order.headquartersRemarks}
                        onChange={(e) =>
                          setOrderList((prev) =>
                            cloneListRewrite(prev, "headquartersRemarks", e.target.value, index),
                          )
                        }
                        fullWidth
                        error={!!order.errors.remarks}
                        inputProps={{ maxLength: 256 }}
                      />
                    </Box>
                  </Box>
                </TableCellWide>
              </TableRow>
            </Fragment>
          );
        })}
      </TableBody>
    </Fragment>
  );

  // 一般用品
  const generalTable = () => (
    <>
      <TableHead>
        <TableRow>
          <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
            商品コード検索
          </TableHeaderCellNarrow>
          {!!customerOrder && <TableHeaderCellNarrow sx={styles.supplierTopCol}>注文番号</TableHeaderCellNarrow>}
          <TableHeaderCellNarrow sx={styles.supplierTopCol} colSpan={customerOrder ? 1 : 2}>商品コード</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>JANコード</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>大分類</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>発注先</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol} colSpan={3}>
            ｼｮｳﾋﾝﾒｲ(半角ｶﾅ)
          </TableHeaderCellNarrow>
          <TableHeaderCellWide center sx={styles.supplierTopCol} rowSpan={2}>
            削除
          </TableHeaderCellWide>
        </TableRow>
        <TableRow>
          <TableHeaderCellNarrow right sx={styles.supplierTopCol}>
            発注数(
            <Typography display="inline" color="red">
              必須
            </Typography>
            )
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            仕切単価
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            本部原単価
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            店舗売単価
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            店舗在庫
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            物流センター在庫
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            仕入先在庫
            <br />
            センター倉庫
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            仕入先在庫
            <br />
            近隣倉庫
          </TableHeaderCellNarrow>
        </TableRow>
      </TableHead>

      <TableBody>
        {orderList.map((order, index) => {
          const isZebraStripeRow = index % 2 === 1;
          // eslint-disable-next-line no-param-reassign
          order = order as OrderGeneralSixBill;
          return (
            <>
              <TableRow key={order.id}>
                <TableCellNarrow center gray={isZebraStripeRow} rowSpan={4}>
                  <Box sx={{ cursor: "pointer" }} onClick={() => handleSearch(index)}>
                    <Search />
                  </Box>
                </TableCellNarrow>
                { !!customerOrder &&
                  <TableCellNarrow gray={isZebraStripeRow}>
                    <CustomTextField
                      id="order_number"
                      label="注文番号"
                      text={order.orderPlainNumber ?? ""}
                      setText={(val: string) =>
                        setOrderList((prev) =>
                          cloneListRewrite(
                            prev as OrderGeneralSixBill[],
                            "orderPlainNumber",
                            val,
                            index,
                          ),
                        )
                      }
                      isNumericText
                      // paleDisabled={!customerOrder}
                      error={order.errors.order_no}
                    />
                  </TableCellNarrow>
                }
                <TableCellNarrow gray={isZebraStripeRow} colSpan={customerOrder ? 1 : 2}>
                  <CustomTextField
                    id="product_cd"
                    label="商品コード"
                    text={order.productCd}
                    setText={(val: string) => {
                      setOrderList((prev) => cloneListRewrite(prev, "productCd", val, index));
                      handleInputCompletion(index, false, { pCd: val });
                    }}
                    isNumericText
                    error={order.errors.item_cd}
                  />
                </TableCellNarrow>
                <TableCellNarrow gray={isZebraStripeRow}>
                  <CustomTextField
                    id="jan_cd"
                    label="JANコード"
                    text={order.janCd}
                    setText={(val: string) => {
                      setOrderList((prev) => cloneListRewrite(prev, "janCd", val, index));
                      handleInputCompletion(index, false, { jCd: val });
                    }}
                    maxLength={100}
                    error={order.errors.jan_cd}
                  />
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.classification}
                </TableCellNarrow>
                <TableCellNarrow gray={isZebraStripeRow} sx={{ px: 0, py: 0 }}>
                  { order.productCd.startsWith("95")
                    ?
                    //   <IncrementalSearchElement
                    //     key="supplier_name"
                    //     label="発注先"
                    //     options={wholeSupplierOptions.map((so) => so.supplierName)}
                    //     value={order.supplierName}
                    //     onChange={(val: string) =>
                    //       setOrderList((prev) => cloneListRewrite(prev, "supplierName", val, index))
                    //     }
                    //     optionLength={10}
                    //     isBlankNoOptions
                    //     fullWidth
                    //   />
                      <CustomTextField
                        id="supplier_cd"
                        label="発注先コード"
                        maxLength={6}
                        text={order.supplierCd ?? ''}
                        setText={(val: string) => {
                          setOrderList((prev) => cloneListRewrite(prev, "supplierCd", val, index))
                        }}
                        isNumericText
                        sx={{px: 1}}
                        // fullWidth
                      />
                    : <SelectForm
                        key="supplier_name"
                        id="supplier_name"
                        label="発注先"
                        optionList={order.supplierOptions.map((c) => c.supplierName)}
                        value={order.supplierName}
                        setValue={(val: string) =>
                          setOrderList((prev) => cloneListRewrite(prev, "supplierName", val, index))
                        }
                        fullWidth
                        minWidth={80}
                        allowValueNotInOptions
                      />
                  }
                </TableCellNarrow>
                <TableCellNarrow gray={isZebraStripeRow} colSpan={3}>
                  <CustomTextField
                    id="product_name"
                    label="ｼｮｳﾋﾝﾒｲ(半角ｶﾅ)"
                    text={order.productName}
                    setText={(val: string) =>
                      setOrderList((prev) => cloneListRewrite(prev, "productName", val, index))
                    }
                    fullWidth
                    maxWidth={400}
                    error={order.errors.item_name}
                    allowHalfKanaOnly
                  />
                </TableCellNarrow>
                <TableCellNarrow center gray={isZebraStripeRow} rowSpan={4}>
                  <Box sx={{ cursor: "pointer" }} onClick={() => handleDelete(index)}>
                    <Delete />
                  </Box>
                </TableCellNarrow>
              </TableRow>
              <TableRow>
                <TableCellNarrow right gray={isZebraStripeRow}>
                  <CustomTextField
                    type="Number"
                    id="order_count"
                    label="発注数"
                    text={`${order.orderCount}`}
                    setText={(val) => {
                      if (val < 0 || val > 99999) {
                        // 発注数は5桁まで
                        return;
                      }
                      setOrderList((prev) => cloneListRewrite(prev, "orderCount", val, index))
                    }}
                    isNumericText
                    intMax={99999}
                    error={order.errors.order_quantity}
                  />
                </TableCellNarrow>
                <TableCellNarrow center gray={isZebraStripeRow}>
                  <CustomTextField
                    type="Number"
                    id="wholesale_unit_price"
                    label="仕切単価"
                    text={`${order.wholesaleUnitPrice}`}
                    setText={(val) => {
                      if (val < 0 || val > 9999999) {
                        // 発注数は7桁まで
                        return;
                      }
                      setOrderList((prev) =>
                        cloneListRewrite(prev, "wholesaleUnitPrice", val, index),
                      )
                    }}
                    isNumericText
                    intMax={9999999}
                    error={order.errors.unit_cost_price}
                  />
                </TableCellNarrow>
                <TableCellNarrow center gray={isZebraStripeRow}>
                  <CustomTextField
                    type="Number"
                    id="headquarters_unit_cost"
                    label="本部原単価"
                    text={`${order.headquartersUnitCost}`}
                    setText={(val) => {
                      if (val < 0 || val > 9999999) {
                        // 発注数は7桁まで
                        return;
                      }
                      setOrderList((prev) =>
                        cloneListRewrite(prev, "headquartersUnitCost", val, index),
                      )
                    }}
                    isNumericText
                    intMax={9999999}
                    error={order.errors.unit_base_price}
                  />
                </TableCellNarrow>
                <TableCellNarrow center gray={isZebraStripeRow}>
                  <CustomTextField
                    type="Number"
                    id="shop_unit_price"
                    label="店舗売単価"
                    text={`${order.shopUnitPrice}`}
                    setText={(val) => {
                      if (val < 0 || val > 9999999) {
                        // 発注数は7桁まで
                        return;
                      }
                      setOrderList((prev) => cloneListRewrite(prev, "shopUnitPrice", val, index))
                    }}
                    isNumericText
                    intMax={9999999}
                    error={order.errors.unit_sale_price}
                  />
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {isClient ? "" : localeStringDisZero(order.shopStock)}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {localeStringDisZero(order.headquartersStock)}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {localeStringDisZero(order.supplierStock.center)}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {localeStringDisZero(order.supplierStock.neighborhood)}
                </TableCellNarrow>
              </TableRow>
              <TableRow>
                <TableCellWide gray={isZebraStripeRow} colSpan={8}>
                  <Box display="flex" flexDirection="row">
                    <Checkbox
                      color="secondary"
                      onChange={(_, check) => {
                        if (check)
                          setOrderList((prev) =>
                            cloneListRewrite(
                              prev,
                              "shopRemarks",
                              `${order.shopRemarks} 電話/FAXで注文済み `,
                              index,
                            ),
                          );
                      }}
                      inputProps={{ "aria-label": "order-by-tel-or-fax-selected" }}
                    />
                    <Box minWidth={140} alignContent="center">
                      <Typography>電話/FAXで注文済</Typography>
                    </Box>
                    <Box width="100%" margin={1}>
                      <TextField
                        type="text"
                        label="店舗備考"
                        size="small"
                        value={order.shopRemarks}
                        onChange={(e) =>
                          setOrderList((prev) =>
                            cloneListRewrite(prev, "shopRemarks", e.target.value, index),
                          )
                        }
                        fullWidth
                        error={!!order.errors.memo}
                        inputProps={{ maxLength: 256 }}
                      />
                    </Box>
                  </Box>
                </TableCellWide>
              </TableRow>
              <TableRow>
                <TableCellWide gray={isZebraStripeRow} colSpan={8}>
                  <Box display="flex" flexDirection="row">
                    <Checkbox
                      color="secondary"
                      onChange={(_, check) => {
                        if (check)
                          setOrderList((prev) =>
                            cloneListRewrite(
                              prev,
                              "headquartersRemarks",
                              `${order.headquartersRemarks} 電話/FAXで注文済み `,
                              index,
                            ),
                          );
                      }}
                      inputProps={{ "aria-label": "order-by-tel-or-fax-selected" }}
                    />
                    <Box minWidth={140} alignContent="center">
                      <Typography>電話/FAXで注文済</Typography>
                    </Box>
                    <Box width="100%" margin={1}>
                      <TextField
                        type="text"
                        label="本部備考"
                        size="small"
                        value={order.headquartersRemarks}
                        onChange={(e) =>
                          setOrderList((prev) =>
                            cloneListRewrite(prev, "headquartersRemarks", e.target.value, index),
                          )
                        }
                        fullWidth
                        error={!!order.errors.remarks}
                        inputProps={{ maxLength: 256 }}
                      />
                    </Box>
                  </Box>
                </TableCellWide>
              </TableRow>
            </>
          );
        })}
      </TableBody>
    </>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose()}
        fullScreen
        PaperProps={{ sx: { position: "relative" } }}
      >
        <header>
          <Header />
        </header>
        <Box width="100%" height={28} textAlign="right">
          <Button
            variant="custom-outlined"
            onClick={async (e) => {
              const url = await downloadHelpPdf(`${getUserType()}/resources/orders/manual_order_product_create_csv.pdf`);
              window.open(url, "_blank", "noopener,noreferrer");
            }}
            startIcon={<HelpOutline color="secondary" />}
            sx={{ height: 24, padding: 1, mb: -1, marginTop: 0.5, marginRight: 1 }}
          >
            CSVヘルプ
          </Button>
          <Button
            variant="custom-outlined"
            onClick={async (e) => {
              const url = await downloadHelpPdf(`${getUserType()}/resources/orders/manual_order_product_create.pdf`);
              window.open(url, "_blank", "noopener,noreferrer");
            }}
            startIcon={<HelpOutline color="secondary" />}
            sx={{ height: 24, padding: 1, mb: -1, marginTop: 0.5, marginRight: 2 }}
          >
            ヘルプ
          </Button>
        </Box>
        <DialogTitle position="relative">
          <Box sx={styles.leftButtonContainer}>
            <Box mx={1}>
              <Button onClick={() => isBackClose ? onClose() : navigate(-1)} variant="custom-outlined">
                <Box>＜ 戻る</Box>
              </Button>
            </Box>
          </Box>
          <Typography component="h2" textAlign="center" fontSize={22}>{title}</Typography>
          <Box position="absolute" top={13} left={0} width="calc(100% - 8px)">
            <Box sx={styles.buttonContainer}>
              {(() => {
                const isSelectedClient = !!(shopCd && isClient); // 得意先を選択しているかどうか
                const isSelectedShop = !!(shopCd && !isClient); // 店舗を選択しているかどうか
                const isNotSelected = !shopCd // 選択していない場合
                const bgColor = "#ffffdd";
                const textColor = "#000000";
                return (
                  <Tooltip
                    title={isNotSelected ? "店舗名を先に選択してください" : isSelectedShop ? "店舗の場合、発送先コードは必要ありません" : ""}
                    arrow
                    placement="top"
                    // disableHoverListener={isNotSelected}
                    // disableFocusListener={isNotSelected}
                    // disableTouchListener={isNotSelected}
                    enterTouchDelay={0}
                    leaveTouchDelay={1000}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: bgColor,
                          color: textColor,
                          fontSize: 14,
                        },
                      },
                      arrow: {
                        sx: {
                          color: bgColor,
                        },
                      },
                    }}
                  >
                    <Box mx={1}>
                      <Button
                        onClick={async () => {
                          setIsOpenDestinationCd(true);
                          // await getDestination(shopCd);
                        }}
                        variant="contained"
                        disabled={!isSelectedClient}
                      >
                        <Box>発送先コードを表示</Box>
                      </Button>
                    </Box>
                  </Tooltip>
                )
              })()}
              {!isEditModeModal && (
                <>
                  <Box mx={1}>
                    <Button
                      onClick={() =>
                        downloadOrderSampleCsv(isEditModeModal ? selectedOrderNumber : undefined)
                      }
                      variant="contained"
                    >
                      <Box>サンプルCSVダウンロード</Box>
                    </Button>
                  </Box>
                  <Box mx={1}>
                    <Button onClick={() => setIsPlaceOrderCsv(true)} variant="contained">
                      <Box>発注CSVアップロード</Box>
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Box textAlign="center" justifyItems="center" my={3}>
              <LabeledButtons
                id="client_or_yh_shop"
                label="YH店舗/得意先"
                value={!isClient ? YHClientOptions[0] : YHClientOptions[1]}
                onChange={(val) => {
                  const currentIsYHShop = val === YHClientOptions[0];
                  if (!currentIsYHShop && productType === "一般用品") {
                    if (
                      orderList.length > 1 || (orderList.length > 0 && (orderList[0].productCd !== "" || orderList[0].janCd !== "" ||
                        ("orderPlainNumber" in orderList[0] && orderList[0].orderPlainNumber !== "" && orderList[0].orderPlainNumber != null)
                      ))
                    ) {
                      console.log('タイヤ・ホイールに変更確認');
                      setIsOpenSwitchProductType(true);
                      setProductTypeBackup("タイヤ・ホイール");
                      setIsYHShopBackup(true);
                    } else {
                      console.log('タイヤ・ホイールに自動変更');
                      switchProductType(true);
                      setProductType("タイヤ・ホイール");
                      setShopCd('');
                      setIsClient(true);
                    }
                  } else {
                    console.log("YH店舗/得意先に変更");
                    setShopCd('');
                    setIsClient(!currentIsYHShop);
                  }
                }}
                options={YHClientOptions}
                // ToDo: 得意先封じ
                // disabled={isEditModeModal}
                disabled
              />
              {/* {!isEditMode && ( */}
                <LabeledIncrementalSearch
                  label="店舗名"
                  value={shopOptions.find((s) => s.clientCd === shopCd)?.shopName ?? ""}
                  options={shopOptions.filter(so => so.isClient === isClient).map((s) => s.shopName)}
                  onChange={async (val: string) => {
                    const cCd = shopOptions.find((so) => so.shopName === val)?.clientCd ?? "";
                    const isC = !!shopOptions.find((so) => so.shopName === val)?.isClient;
                    setShopCd(cCd);
                    setIsClient(isC);
                    if (isC) {
                      switchProductType(true);
                      setProductType("タイヤ・ホイール");
                      await getDestination(cCd);
                    } else {
                      await getDestinationInfo(cCd);
                    }
                  }}
                  disabled={isEditModeModal}
                />
              {/* )} */}
              {/* <LabeledSelect
                id="general_or_tyre_wheel"
                label="一般用品/タイヤ・ホイール"
                value={productType}
                onChange={(val) => {
                  if (orderList.length > 1 || (orderList.length > 0 && (orderList[0].productCd !== "" || orderList[0].janCd !== ""))) {
                    setIsOpenSwitchProductType(true);
                    setProductTypeBackup(val);
                  } else {
                    switchProductType(val === "タイヤ・ホイール");
                    setProductType(val);
                  }
                }}
                options={productTypeOptions}
                disabled={isEditModeModal || (isClient && !checkGeneralBill())}
              /> */}
              <LabeledButtons
                id="general_or_tyre_wheel"
                label="一般用品/タイヤ・ホイール"
                value={productType}
                onChange={(val) => {
                  if (
                    orderList.length > 1 || (orderList.length > 0 && (orderList[0].productCd !== "" || orderList[0].janCd !== "" || ("orderPlainNumber" in orderList[0] && orderList[0].orderPlainNumber !== "" && orderList[0].orderPlainNumber != null)))
                  ) {
                    setIsOpenSwitchProductType(true);
                    setProductTypeBackup(val);
                  } else {
                    switchProductType(val === "タイヤ・ホイール");
                    setProductType(val);
                  }
                }}
                options={productTypeOptions}
                disabled={isEditModeModal || (isClient && !checkGeneralBill())}
              />
            </Box>
            <TableContainer
              component={Paper}
              sx={{ overflowX: "initial", width: "fit-content", minWidth: "100%" }}
            >
              <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="order table">
                {orderList.length !== 0 && (checkGeneralBill() ? generalTable() : tyreTable())}
              </Table>
            </TableContainer>
            <Box width="100%" textAlign="center" my={2} minHeight="10px" position="relative">
              <Box position="absolute" right={0} textAlign="right" >
                ※原価、卸金額は0または空欄の場合のみ再取得/再計算を実施します。<br />
                ※代表コードの場合は、必ず備考欄を入力してください。
              </Box>
              {!checkFullCost(5.5) && ( // セットも含めてクリックした後に6以下になる必要があるので
                <Box mx={1}>
                  <Button
                    onClick={async () =>
                      setOrderList((prev) => {
                        const newOrder = structuredClone(prev);
                        newOrder.push(
                          checkGeneralBill()
                            ? defaultGeneralOrder(prev.length)
                            : defaultTyreOrder(prev.length),
                        );
                        return newOrder;
                      })
                    }
                    variant="custom-outlined"
                  >
                    <Box>＋行を追加</Box>
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
          <Box textAlign="center" justifyItems="center" my={3}>
            {checkGeneralBill() && (
              <LabeledComponent label="客注">
                <Checkbox
                  color="secondary"
                  checked={customerOrder}
                  onChange={(_, check) => {
                    setCustomerOrder(check);
                    if (!check) {
                      setOrderList((prev: OrderSixBill[]) => {
                        let newOrders = structuredClone(prev) as OrderGeneralSixBill[];
                        newOrders = newOrders.map(o => {
                          // eslint-disable-next-line no-param-reassign
                          o.orderPlainNumber = "";
                          return o;
                        });
                        return newOrders;
                      });
                    }
                  }}
                  inputProps={{ "aria-label": "customer-order-selected" }}
                />
              </LabeledComponent>
            )}
            <LabeledTextField
              id="shop_orderer"
              type="text"
              label="店舗発注者"
              text={shopOrderer}
              setText={setShopOrderer}
              textRequired={checkGeneralBill()}
              error={shopOrdererError}
            />
            <LabeledTextField
              id="approver"
              type="text"
              label="承認者"
              text={approver}
              setText={setApprover}
              textRequired={checkGeneralBill()}
              error={approverError}
            />
            <LabeledComponent
              label="納品希望日"
              description="※土日祝を除く翌々日以降を選択してください。"
            >
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                <DatePicker
                  label="納品希望日"
                  format="YYYY/MM/DD"
                  minDate={addBusinessDate(dayjs(), 2)}
                  value={requestDeliveryDate ? dayjs(requestDeliveryDate, "YYYY/MM/DD") : null}
                  onChange={(val) => setRequestDeliveryDate(val ? val.format("YYYY/MM/DD") : "")}
                  slotProps={{ textField: { size: "small" } }}
                  sx={{
                    width: "207px",
                  }}
                  shouldDisableDate={(date) => {
                    if (!checkBusinessDate(date)) {
                      return true; // 営業日でない場合は選択不可
                    }
                    return false;
                  }}
                  onError={(reason) => {
                    setDeliveryDateError(reason ? "納品希望日の日付が無効です。" : null);
                  }}
                />
              </LocalizationProvider>
            </LabeledComponent>
            {checkGeneralBill() && (
              <>
                {/* <LabeledComponent
                  label="出荷予定日"
                  description="※土日祝を除く翌々日以降を選択してください。"
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                    <DatePicker
                      label="出荷予定日"
                      format="YYYY/MM/DD"
                      minDate={dayjs()}
                      value={
                        expectedShippingDate ? dayjs(expectedShippingDate, "YYYY/MM/DD") : null
                      }
                      onChange={(val) =>
                        setExpectedShippingDate(val ? val.format("YYYY/MM/DD") : "")
                      }
                      slotProps={{ textField: { size: "small" } }}
                      sx={{
                        width: "207px",
                      }}
                      shouldDisableDate={(date) => {
                        if (!checkBusinessDate(date)) {
                          return true; // 営業日でない場合は選択不可
                        }
                        return false;
                      }}
                      onError={(reason) => {
                        setExpectedShippingDateError(
                          reason ? "出荷予定日の日付が無効です。" : null,
                        );
                      }}
                    />
                  </LocalizationProvider>
                </LabeledComponent> */}
                <LabeledSelect
                  id="request_direct_delivery"
                  label="直送"
                  value={requestDirectDelivery}
                  onChange={(val: DirectDelivery) => setRequestDirectDelivery(val)}
                  options={directDeliveryOptions.slice(0,2)}
                />
                {/* <LabeledComponent label="直送">
                  // <Checkbox
                  //   color="secondary"
                  //   checked={isDirectDelivery}
                  //   onChange={(_, check) => setIsDirectDelivery(check)}
                  //   inputProps={{ "aria-label": "customer-order-selected" }}
                  // />
                  <Box>
                    <Typography>{isDirectDelivery === true ? "メーカー直送で納品" : (isDirectDelivery === false ? "物流経由で納品" : "未定")}</Typography>
                  </Box>
                </LabeledComponent> */}
              </>
            )}
            <LabeledTextField
              id="client_bill_number"
              type="text"
              label="得意先伝票No."
              text={clientBillNumber}
              setText={setClientBillNumber}
            />
            {/* <LabeledTextField
              id="fax_number"
              type="text"
              label="店舗FAX番号"
              text={faxNumber}
              setText={setFaxNumber}
              description="※半角、ﾊｲﾌﾝ有り"
            /> */}
            <Box mb={4} />
            <Accordion sx={{ width: "80%" }}>
              <AccordionSummary
                expandIcon={<ArrowDropDown />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    width: "100%",
                  }}
                >
                  <Typography variant="subtitle1">発送先</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 2, whiteSpace: "nowrap" }}
                  >
                    ※納品先変更ありの場合はこちら
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Box textAlign="center" justifyItems="center" my={3}>
                  {!checkGeneralBill() && isClient && (
                    <LabeledSelect
                      id="destination_cd"
                      label="発送先コード"
                      value={shippingDestination}
                      options={(() => {
                        const options: string[] = [];
                        if (destinationCheckData.A && destinationCheckData.A.destinationCd)
                          options.push(
                            `${destinationCheckData.A.destinationCd} ${destinationCheckData.A.destinationName}`,
                          );
                        if (destinationCheckData.B && destinationCheckData.B.destinationCd)
                          options.push(
                            `${destinationCheckData.B.destinationCd} ${destinationCheckData.B.destinationName}`,
                          );
                        if (destinationCheckData.C && destinationCheckData.C.destinationCd)
                          options.push(
                            `${destinationCheckData.C.destinationCd} ${destinationCheckData.C.destinationName}`,
                          );
                        return options;
                      })()}
                      onChange={(val: string) => {
                        const v = val.split(" ")[0]; // 発送先コードを取得
                        setShippingDestination(val);
                        // eslint-disable-next-line no-restricted-syntax
                        for (const abc of (['A','B','C'] as const)) {
                          const destData = destinationCheckData[abc];
                          if (destData && destData.destinationCd === v){
                            setDestinationAddress(destData.destinationAddress);
                            setDestinationName(destData.destinationName);
                            break;
                          }
                        }
                      }}
                      isContainBlank
                    />
                  )}
                  <LabeledComponent label="郵便番号" description="※半角、ﾊｲﾌﾝ有り">
                    <CustomTextField
                      id="postal_cd"
                      text={postalCd}
                      label="郵便番号"
                      setText={(value) => setPostalCd(value)}
                      sx={{ maxWidth: 207 }}
                      isPostalCd
                    />
                  </LabeledComponent>
                  <LabeledTextField
                    id="destination_address"
                    type="text"
                    label="発送先住所"
                    text={destinationAddress}
                    setText={setDestinationAddress}
                    description="※全角"
                    maxLength={256}
                  />
                  <LabeledTextField
                    id="phone_number"
                    type="text"
                    label="発送先電話番号"
                    text={phoneNumber}
                    setText={setPhoneNumber}
                    description="※半角、ﾊｲﾌﾝ有り"
                    allowHalfKanaOnly
                    maxLength={20}
                  />
                  <LabeledTextField
                    id="destination_name"
                    type="text"
                    label="発送先名"
                    text={destinationName}
                    setText={setDestinationName}
                    description="※全角"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>{actionButtons()}</DialogActions>
        {isLoading && ( // マスク
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.2)",
              zIndex: (theme) => theme.zIndex.modal + 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Dialog>

      {/* 発送先コード確認モーダル */}
      <Dialog
        open={isOpenDestinationCd}
        onClose={() => {
          setIsOpenDestinationCd(false);
        }}
      >
        <DialogTitle>発送先コード確認</DialogTitle>
        {isDestinationLoading ? (
          <DialogContent sx={{alignContent: "center", textAlign: "center"}}>
            <Box py={3} textAlign="center">
              <CircularProgress />
            </Box>
          </DialogContent>
        ) : (
          <DialogContent>
            <Box>
              {destinationCheckData.A && (
                <Surround title="指定発送先A">
                  <Box my={1} mx={2}>
                    <LabeledText label="発送先コード" text={destinationCheckData.A.destinationCd} width="inherit" />
                    <LabeledText label="発送先名" text={destinationCheckData.A.destinationName} width="inherit" />
                    <LabeledText label="住所" text={destinationCheckData.A.destinationAddress} width="inherit" />
                  </Box>
                </Surround>
              )}
              {destinationCheckData.B && (
                <Surround title="指定発送先B">
                  <Box my={1} mx={2}>
                    <LabeledText label="発送先コード" text={destinationCheckData.B.destinationCd} width="inherit" />
                    <LabeledText label="発送先名" text={destinationCheckData.B.destinationName} width="inherit" />
                    <LabeledText label="住所" text={destinationCheckData.B.destinationAddress} width="inherit" />
                  </Box>
                </Surround>
              )}
              {destinationCheckData.C && (
                <Surround title="指定発送先C">
                  <Box my={1} mx={2}>
                    <LabeledText label="発送先コード" text={destinationCheckData.C.destinationCd} width="inherit" />
                    <LabeledText label="発送先名" text={destinationCheckData.C.destinationName} width="inherit" />
                    <LabeledText label="住所" text={destinationCheckData.C.destinationAddress} width="inherit" />
                  </Box>
                </Surround>
              )}
            </Box>
          </DialogContent>
        )}
      </Dialog>

      {/* 在庫詳細モーダル */}
      <Dialog
        open={isOpenStockDetail}
        onClose={() => {
          setSelectedIndex(undefined);
          setIsOpenStockDetail(false);
        }}
      >
        <DialogTitle>在庫詳細</DialogTitle>
        <DialogContent>
          {(() => {
            if (orderList.length > 0 && selectedIndex !== undefined) {
              const stockDict = orderList[selectedIndex ?? 0]
                .headquartersStock as OrderTyreSixBill["headquartersStock"];
              return typeof stockDict === "number" ? (
                <Box />
              ) : (
                <Box border="1px solid var(--offwhite-600)">
                  <TableRow sx={{}}>
                    <TableHeaderCellSmall>明和倉庫</TableHeaderCellSmall>
                    <TableCellWide>{localeStringDisZero(stockDict.meiwa)}</TableCellWide>
                  </TableRow>
                  <TableRow>
                    <TableHeaderCellSmall>加西倉庫</TableHeaderCellSmall>
                    <TableCellWide>{localeStringDisZero(stockDict.kasai)}</TableCellWide>
                  </TableRow>
                  <TableRow>
                    <TableHeaderCellSmall>東北倉庫</TableHeaderCellSmall>
                    <TableCellWide>{localeStringDisZero(stockDict.tohoku)}</TableCellWide>
                  </TableRow>
                  <TableRow>
                    <TableHeaderCellSmall>伊藤忠倉庫</TableHeaderCellSmall>
                    <TableCellWide>{localeStringDisZero(stockDict.itochu)}</TableCellWide>
                  </TableRow>
                  <TableRow>
                    <TableHeaderCellSmall>昭和製氷倉庫</TableHeaderCellSmall>
                    <TableCellWide>{localeStringDisZero(stockDict.showa)}</TableCellWide>
                  </TableRow>
                </Box>
              );
            }
            return <Box />;
          })()}
        </DialogContent>
      </Dialog>

      {/* 商品コード検索モーダル */}
      <ProductCdSearch
        isOpenProductCdSearch={isOpenProductCdSearch}
        setIsOpenProductCdSearch={setIsOpenProductCdSearch}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        classificationOptions={classificationOptions}
        brandOptions={brandOptions}
        tyreOptions={tyreOptions}
        wheelOptions={wheelOptions}
        setProductCd={(pCd, sIndex) => {
          setOrderList((prev) => cloneListRewrite(prev, "productCd", pCd, sIndex));
          handleInputCompletion(sIndex, !checkGeneralBill(), { pCd });
        }}
        productType={productType}
        clientCd={shopCd}
      />

      {/* タイヤ・ホイール/一般用品を切り替えた時にデータが削除される旨を警告するモーダル */}
      <Dialog
        open={isOpenSwitchProductType}
        onClose={() => {
          setProductTypeBackup("タイヤ・ホイール");
          setIsOpenSwitchProductType(false);
          if (isYHShopBackup) {
            setIsYHShopBackup(false);
            setShopCd('');
            setIsClient(true); // 得意先に変更
          }
        }}
      >
        <DialogTitle>切り替え確認</DialogTitle>
        <DialogContent>
          {isYHShopBackup
            ? "一般店ではタイヤ・ホイールのみ許可されますので、一般用品 => タイヤ・ホイールに切り替わり、入力データが削除されます。よろしいですか？"
            : "「タイヤ・ホイール/一般用品」を切り替えると入力データが削除されます。よろしいですか？"}
        </DialogContent>
        <DialogActions>
          <Button
            variant="custom-outlined"
            onClick={() => {
              setProductTypeBackup("タイヤ・ホイール");
              setIsOpenSwitchProductType(false);
              if (isYHShopBackup) {
                setIsYHShopBackup(false);
              }
            }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              switchProductType(productTypeBackup === "タイヤ・ホイール");
              setProductType(productTypeBackup);
              setProductTypeBackup("タイヤ・ホイール");
              setIsOpenSwitchProductType(false);
              if (isYHShopBackup) {
                setIsYHShopBackup(false);
                setShopCd('');
                setIsClient(true); // 得意先に変更
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* 発注CSVモーダル */}
      <CsvModals
        refresh={() => {}}
        isOpenPlaceOrderCsvUpload={isPlaceOrderCsv}
        setIsOpenPlaceOrderCsvUpload={setIsPlaceOrderCsv}
      />
    </>
  );
}
